// src/App.js
import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import Fab from '@mui/material/Fab';
import ChatIcon from '@mui/icons-material/Chat';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';

import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import WhatsAppButton from './components/WhatsAppButton';
import Footer from './components/Footer';

import RequireAdmin from './routes/RequireAdmin';

const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegistroPage = lazy(() => import('./pages/RegistroPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ForumPage = lazy(() => import('./pages/ForumPage'));
const ThreadPage = lazy(() => import('./pages/ThreadPage'));
const SolutionDetailPage = lazy(() => import('./pages/SolutionDetailPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const WebinarRegistrationPage = lazy(() => import('./pages/WebinarRegistrationPage'));
const NotAuthorized = lazy(() => import('./pages/NotAuthorized'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const NewsDetail = lazy(() => import('./pages/NewsDetail'));
const ChatWidget = lazy(() => import('./chatbot/components/ChatWidget'));

const initialThreads = [];
const analyticsMeasurementId = 'G-Z9LCKFH25F';
let analyticsPromise;

function loadAnalytics() {
  if (!analyticsPromise) {
    analyticsPromise = import('react-ga4').then(({ default: ReactGA }) => {
      ReactGA.initialize(analyticsMeasurementId, {
        gtagOptions: { send_page_view: false },
      });
      return ReactGA;
    });
  }
  return analyticsPromise;
}

function RouteFallback() {
  return (
    <Box
      sx={{ minHeight: '45vh', display: 'grid', placeItems: 'center' }}
    >
      <CircularProgress size={34} thickness={4} />
    </Box>
  );
}

function DeferredChatWidget() {
  const [isLoaded, setIsLoaded] = useState(false);

  if (!isLoaded) {
    return (
      <Fab
        color="primary"
        onClick={() => setIsLoaded(true)}
        aria-label="Open chat"
        sx={{
          position: 'fixed',
          right: 25,
          bottom: 25,
          zIndex: 1300,
          bgcolor: '#1473c9',
          boxShadow: '0 6px 20px rgba(15, 67, 130, 0.28)',
          transition: 'transform 180ms ease, background-color 180ms ease',
          '&:hover': { bgcolor: '#0f5fae', transform: 'translateY(-2px)' },
        }}
      >
        <ChatIcon />
      </Fab>
    );
  }

  return (
    <Suspense fallback={
      <Fab
        color="primary"
        disabled
        aria-label="Loading chat"
        sx={{ position: 'fixed', right: 25, bottom: 25, zIndex: 1300 }}
      >
        <CircularProgress size={22} color="inherit" />
      </Fab>
    }>
      <ChatWidget initiallyOpen />
    </Suspense>
  );
}

/* ---- Wrapper para ocultar componentes en rutas admin ---- */
function AppInner() {
  const location = useLocation();

  useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return undefined;

    let cancelled = false;
    const sendPageView = () => {
      loadAnalytics().then((ReactGA) => {
        if (!cancelled) {
          ReactGA.send({
            hitType: 'pageview',
            page: location.pathname + location.search,
          });
        }
      });
    };

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(sendPageView, { timeout: 2500 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }

    const timeoutId = window.setTimeout(sendPageView, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [location.pathname, location.search]);

  // Oculta en /admin (y, si quieres también, en /dashboard)
  const isAdminRoute =
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/dashboard'); // quita esta línea si NO quieres ocultar en /dashboard

  const [threads /*, setThreads */] = useState(initialThreads);

  const handleAddThread = (newThreadData) => {};
  const handleAddReply = (threadId, replyContent) => {};
  const handleDeleteThread = (threadId) => {};
  const handleDeleteReply = (threadId, replyId) => {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!isAdminRoute && <Navbar />}

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about-us" element={<AboutUsPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/solutions/:solutionId" element={<SolutionDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms-and-conditions" element={<TermsPage />} />
          <Route path="/webinar" element={<WebinarRegistrationPage />} />

          {/* Foro */}
          <Route
            path="/forum"
            element={
              <ForumPage
                threads={threads}
                handleAddThread={handleAddThread}
                handleDeleteThread={handleDeleteThread}
              />
            }
          />
          <Route
            path="/forum/thread/:threadId"
            element={
              <ThreadPage
                threads={threads}
                handleAddReply={handleAddReply}
                handleDeleteReply={handleDeleteReply}
              />
            }
          />

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/registro" element={<RegistroPage />} />

          {/* Dashboard de usuario (si lo tienes) */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Dashboard de ADMIN protegido por rol */}
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminDashboard />
              </RequireAdmin>
            }
          />

          {/* NEW: detalle de noticias por slug */}
          <Route path="/news/:slug" element={<NewsDetail />} /> {/* NEW */}

          {/* 403 y 404 */}
          <Route path="/403" element={<NotAuthorized />} />
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Box>

      {!isAdminRoute && <WhatsAppButton />}
      {!isAdminRoute && <DeferredChatWidget />}
      {!isAdminRoute && <Footer />}
    </Box>
  );
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <ScrollToTop />
          <AppInner />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
